import getNextConfig from 'next/config';
import type { NextRuntimeConfiguration } from '../../../libs/config';
import { Logger } from '@icommerce/logger';
import type { Header } from '../types';
import { getLowercaseLocale } from './header.helper';

const { publicRuntimeConfig } = getNextConfig() as NextRuntimeConfiguration;

/**
 * Fetches detailed header data for client-side hydration.
 */
export const fetchClientHeaderData = async (locale: string): Promise<Header | null> => {
  const { apiUrl } = publicRuntimeConfig.header;
  const localeSlug = getLowercaseLocale(locale);
  const url = `${apiUrl}/data/navigation?locale=${localeSlug}&sbVersion=published`;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 3000); // 3 seconds

  try {
    const res = await fetch(url, { method: 'GET', signal: controller.signal });

    if (!res.ok) {
      throw new Error(`Failed to fetch client header from ${url} (status: ${res.status})`);
    }

    return (await res.json()) as Header;
  } catch (error) {
    const isTimeout = (error as Error).name === 'AbortError';

    Logger.error(
      {
        message: isTimeout ? 'Fetch timed out' : 'Error fetching client header',
        error: (error as Error).message,
        locale,
      },
      'ErrorFetchingClientHeader'
    );
    return null;
  } finally {
    clearTimeout(timeout);
  }
};
